import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, Input, Select, Alert, Skeleton, Typography } from "antd";
import { HistoryOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  getCommercialDateAnalysisAnnualTotals,
  getCommercialDateAnalysisEvents,
  getCommercialDateAnalysisLandingMetrics,
} from "../../../api/Services";
import {
  CommercialDateAnalysisAnnualTotal,
  CommercialDateAnalysisCard,
  CommercialDateAnalysisEventMeta,
  CommercialDateAnalysisLandingMetrics,
} from "../../../types/commercialDateAnalysis";
import styles from "./CommercialDateAnalysis.module.scss";

const { Option } = Select;
const { Title, Text } = Typography;

const METRICS_CONCURRENCY = 2;
const SESSION_CACHE_TTL_MS = 10 * 60 * 1000;

const sessionKey = (suffix: string) => `commercial-dates:${suffix}`;

function readSessionJson<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { ts: number; data: T };
    if (Date.now() - parsed.ts > SESSION_CACHE_TTL_MS) {
      sessionStorage.removeItem(key);
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

function writeSessionJson<T>(key: string, data: T) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    /* quota exceeded — ignore */
  }
}

const formatMoney = (n: number) =>
  "$" + n.toLocaleString("es-UY", { maximumFractionDigits: 0 });

const formatUnits = (n: number) => n.toLocaleString("es-UY");

const formatVar = (v: number | null | undefined, isPp = false) => {
  if (v == null) return "—";
  const sign = v > 0 ? "+" : "";
  const suffix = isPp ? " pp" : "%";
  return `${sign}${v.toFixed(isPp ? 1 : 0)}${suffix}`;
};

const varClass = (v: number | null | undefined) => {
  if (v == null) return styles.varNeutral;
  if (v > 0.05) return styles.varPositive;
  if (v < -0.05) return styles.varNegative;
  return styles.varNeutral;
};

const round1 = (v: number) => Math.round(v * 10) / 10;

const applyAnnualPcts = (
  card: CommercialDateAnalysisCard,
  annualByYear: Map<number, CommercialDateAnalysisAnnualTotal>
): CommercialDateAnalysisCard => {
  if (card.grossSales == null || card.units == null) return card;

  const currentAnnual = annualByYear.get(card.editionYear);
  const priorAnnual = card.hasPriorEditionComparison
    ? annualByYear.get(card.editionYear - 1)
    : undefined;

  const annualSalesMoneyPct =
    currentAnnual && currentAnnual.sales > 0
      ? (card.grossSales / currentAnnual.sales) * 100
      : 0;
  const annualSalesUnitsPct =
    currentAnnual && currentAnnual.units > 0
      ? (card.units / currentAnnual.units) * 100
      : 0;

  let annualSalesMoneyVarPp: number | null = null;
  let annualSalesUnitsVarPp: number | null = null;
  if (card.hasPriorEditionComparison && card.priorGrossSales != null && card.priorUnits != null) {
    const priorMoneyPct =
      priorAnnual && priorAnnual.sales > 0
        ? (card.priorGrossSales / priorAnnual.sales) * 100
        : 0;
    const priorUnitsPct =
      priorAnnual && priorAnnual.units > 0
        ? (card.priorUnits / priorAnnual.units) * 100
        : 0;
    annualSalesMoneyVarPp = round1(annualSalesMoneyPct - priorMoneyPct);
    annualSalesUnitsVarPp = round1(annualSalesUnitsPct - priorUnitsPct);
  }

  return {
    ...card,
    annualSalesMoneyPct,
    annualSalesUnitsPct,
    annualSalesMoneyVarPp,
    annualSalesUnitsVarPp,
  };
};

const mergeMetrics = (
  card: CommercialDateAnalysisCard,
  metrics: CommercialDateAnalysisLandingMetrics,
  annualByYear: Map<number, CommercialDateAnalysisAnnualTotal>
): CommercialDateAnalysisCard => {
  const withMetrics: CommercialDateAnalysisCard = {
    ...card,
    metricsStatus: "ready",
    grossSales: metrics.grossSales,
    grossSalesVarPct: metrics.grossSalesVarPct,
    units: metrics.units,
    unitsVarPct: metrics.unitsVarPct,
    marginPct: metrics.marginPct,
    marginVarPp: metrics.marginVarPp,
    avgTicket: metrics.avgTicket,
    avgTicketVarPct: metrics.avgTicketVarPct,
    basketSize: metrics.basketSize,
    basketSizeVarPct: metrics.basketSizeVarPct,
    priorGrossSales: metrics.priorGrossSales ?? undefined,
    priorUnits: metrics.priorUnits ?? undefined,
  };
  return applyAnnualPcts(withMetrics, annualByYear);
};

async function loadWithConcurrency<T>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<void>,
  isCancelled: () => boolean
): Promise<void> {
  if (items.length === 0) return;
  let nextIndex = 0;
  const worker = async () => {
    while (nextIndex < items.length) {
      if (isCancelled()) return;
      const current = nextIndex++;
      await fn(items[current]);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, () => worker())
  );
}

const METRIC_SKELETON_ROWS = 7;

const CardMetricsSkeleton: React.FC = () => (
  <div className={styles.metricsSkeleton} aria-hidden="true">
    {Array.from({ length: METRIC_SKELETON_ROWS }).map((_, i) => (
      <div key={i} className={styles.metricRowSkeleton}>
        <span className={`${styles.skeletonBar} ${styles.skeletonLabel}`} />
        <span className={styles.skeletonValueGroup}>
          <span className={`${styles.skeletonBar} ${styles.skeletonValue}`} />
          <span className={`${styles.skeletonBar} ${styles.skeletonVar}`} />
        </span>
      </div>
    ))}
  </div>
);

const CommercialDateCard: React.FC<{
  card: CommercialDateAnalysisCard;
  onOpen: () => void;
}> = ({ card, onOpen }) => {
  const { t } = useTranslation();
  const metricsLoading = card.metricsStatus === "loading";
  const metricsError = card.metricsStatus === "error";

  const rows: { label: string; value: string; var: number | null; isPp?: boolean }[] = [
    {
      label: t("CommercialDateAnalysis-Sale"),
      value: metricsLoading || metricsError ? "—" : formatMoney(card.grossSales ?? 0),
      var: metricsError ? null : card.grossSalesVarPct ?? null,
    },
    {
      label: t("CommercialDateAnalysis-Units"),
      value: metricsLoading || metricsError ? "—" : formatUnits(card.units ?? 0),
      var: metricsError ? null : card.unitsVarPct ?? null,
    },
    {
      label: t("CommercialDateAnalysis-MarginPct"),
      value: metricsLoading || metricsError ? "—" : (card.marginPct ?? 0).toFixed(1) + "%",
      var: metricsError ? null : card.marginVarPp ?? null,
      isPp: true,
    },
    {
      label: t("CommercialDateAnalysis-AverageTicket"),
      value: metricsLoading || metricsError ? "—" : formatMoney(card.avgTicket ?? 0),
      var: metricsError ? null : card.avgTicketVarPct ?? null,
    },
    {
      label: t("CommercialDateAnalysis-BasketSize"),
      value: metricsLoading || metricsError ? "—" : (card.basketSize ?? 0).toFixed(1) + " uds",
      var: metricsError ? null : card.basketSizeVarPct ?? null,
    },
    {
      label: t("CommercialDateAnalysis-AnnualSalesMoney"),
      value:
        metricsLoading || metricsError
          ? "—"
          : (card.annualSalesMoneyPct ?? 0).toFixed(1) + "%",
      var: metricsError ? null : card.annualSalesMoneyVarPp ?? null,
      isPp: true,
    },
    {
      label: t("CommercialDateAnalysis-AnnualSalesUnits"),
      value:
        metricsLoading || metricsError
          ? "—"
          : (card.annualSalesUnitsPct ?? 0).toFixed(1) + "%",
      var: metricsError ? null : card.annualSalesUnitsVarPp ?? null,
      isPp: true,
    },
  ];

  return (
    <article
      className={`${styles.eventCard}${metricsLoading ? ` ${styles.eventCardLoading}` : ""}`}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => e.key === "Enter" && onOpen()}
      aria-busy={metricsLoading}
    >
      <div className={styles.eventCardHeader}>
        <div>
          <h3 className={styles.eventName}>{card.name}</h3>
          <p className={styles.eventDateRange}>{card.dateRangeLabel}</p>
        </div>
        <HistoryOutlined className={styles.historyIcon} />
      </div>
      {metricsLoading ? (
        <CardMetricsSkeleton />
      ) : (
        rows.map((row) => (
          <div key={row.label} className={styles.metricRow}>
            <span className={styles.metricLabel}>{row.label}</span>
            <span>
              <span className={styles.metricValue}>{row.value}</span>
              <span className={`${styles.metricVar} ${varClass(row.var)}`}>
                {formatVar(row.var, row.isPp)}
              </span>
            </span>
          </div>
        ))
      )}
      <Button
        className={styles.detailBtn}
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
      >
        {t("CommercialDateAnalysis-ViewDetail")}
      </Button>
    </article>
  );
};

const CommercialDateAnalysis: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [yearFilter, setYearFilter] = useState<number | "all">("all");
  const [eventsLoading, setEventsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [cards, setCards] = useState<CommercialDateAnalysisCard[]>([]);
  const annualByYearRef = useRef<Map<number, CommercialDateAnalysisAnnualTotal>>(new Map());

  const updateCard = useCallback((id: number, updater: (c: CommercialDateAnalysisCard) => CommercialDateAnalysisCard) => {
    setCards((prev) => prev.map((c) => (c.id === id ? updater(c) : c)));
  }, []);

  const reapplyAnnualToAll = useCallback(() => {
    const annualByYear = annualByYearRef.current;
    setCards((prev) =>
      prev.map((c) =>
        c.metricsStatus === "ready" ? applyAnnualPcts(c, annualByYear) : c
      )
    );
  }, []);

  useEffect(() => {
    let cancelled = false;
    const isCancelled = () => cancelled;

    (async () => {
      setEventsLoading(true);
      setError(false);
      setCards([]);

      const cachedEvents = readSessionJson<{ items: CommercialDateAnalysisEventMeta[] }>(
        sessionKey("events")
      );
      const eventsData =
        cachedEvents ?? (await getCommercialDateAnalysisEvents());
      if (isCancelled()) return;

      if (!cachedEvents && eventsData) {
        writeSessionJson(sessionKey("events"), eventsData);
      }

      if (!eventsData?.items) {
        setError(true);
        setEventsLoading(false);
        return;
      }

      const initialCards: CommercialDateAnalysisCard[] = eventsData.items.map((e) => ({
        ...e,
        metricsStatus: readSessionJson<CommercialDateAnalysisLandingMetrics>(
          sessionKey(`metrics:${e.id}`)
        )
          ? ("ready" as const)
          : ("loading" as const),
      }));
      setCards(initialCards);
      setEventsLoading(false);

      for (const card of initialCards) {
        const cachedMetrics = readSessionJson<CommercialDateAnalysisLandingMetrics>(
          sessionKey(`metrics:${card.id}`)
        );
        if (cachedMetrics) {
          updateCard(card.id, (c) => mergeMetrics(c, cachedMetrics, annualByYearRef.current));
        }
      }

      const cachedAnnual = readSessionJson<{ totals: CommercialDateAnalysisAnnualTotal[] }>(
        sessionKey("annual")
      );
      const annualPromise = (cachedAnnual
        ? Promise.resolve(cachedAnnual)
        : getCommercialDateAnalysisAnnualTotals()
      ).then((data) => {
        if (isCancelled() || !data?.totals) return;
        if (!cachedAnnual) {
          writeSessionJson(sessionKey("annual"), data);
        }
        const map = new Map<number, CommercialDateAnalysisAnnualTotal>();
        for (const row of data.totals) {
          map.set(row.year, row);
        }
        annualByYearRef.current = map;
        reapplyAnnualToAll();
      });

      const cardsNeedingMetrics = initialCards.filter(
        (c) => c.metricsStatus === "loading"
      );

      await loadWithConcurrency(
        cardsNeedingMetrics,
        METRICS_CONCURRENCY,
        async (card) => {
          const metrics = await getCommercialDateAnalysisLandingMetrics(card.id);
          if (isCancelled()) return;
          if (!metrics) {
            updateCard(card.id, (c) => ({ ...c, metricsStatus: "error" }));
            return;
          }
          writeSessionJson(sessionKey(`metrics:${card.id}`), metrics);
          updateCard(card.id, (c) =>
            mergeMetrics(c, metrics, annualByYearRef.current)
          );
        },
        isCancelled
      );

      await annualPromise;
    })();

    return () => {
      cancelled = true;
    };
  }, [reapplyAnnualToAll, updateCard]);

  const availableYears = useMemo(() => {
    const years = new Set(cards.map((c) => c.editionYear));
    return Array.from(years).sort((a, b) => b - a);
  }, [cards]);

  const filtered = useMemo(() => {
    let list = cards;
    if (yearFilter !== "all") {
      list = list.filter((cd) => cd.editionYear === yearFilter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((cd) => cd.name.toLowerCase().includes(q));
    }
    return list;
  }, [search, yearFilter, cards]);

  if (eventsLoading) {
    return (
      <div className={styles.wrapper}>
        <Title level={3} className={styles.pageTitle}>
          {t("CommercialDateAnalysis-Title")}
        </Title>

        <Card className={styles.filtersCard} size="small">
          <div className={styles.filtersRow}>
            <Skeleton.Input active style={{ flex: 1, minWidth: 200, maxWidth: 320, height: 32 }} />
            <Skeleton.Button active style={{ minWidth: 160, height: 32 }} />
          </div>
        </Card>

        <div className={styles.cardsGrid}>
          {Array.from({ length: 6 }).map((_, i) => (
            <article key={i} className={`${styles.eventCard} ${styles.skeletonCard}`}>
              <div className={styles.eventCardHeader}>
                <Skeleton.Input active size="small" style={{ width: "70%", height: 18, marginBottom: 8 }} />
                <Skeleton.Input active size="small" style={{ width: "55%", height: 14 }} />
              </div>
              <CardMetricsSkeleton />
              <Skeleton.Button active block style={{ marginTop: 16, height: 32 }} />
            </article>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <Title level={3} className={styles.pageTitle}>
        {t("CommercialDateAnalysis-Title")}
      </Title>

      {error && (
        <Alert
          type="error"
          showIcon
          message={t("Common-ErrorMessage")}
          style={{ marginBottom: 16 }}
        />
      )}

      <Card className={styles.filtersCard} size="small">
        <div className={styles.filtersRow}>
          <Input
            className={styles.searchInput}
            placeholder={t("CommercialDateAnalysis-Search")}
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
          />
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>{t("CommercialDateAnalysis-Year")}</Text>
            <Select
              className={styles.filterSelect}
              value={yearFilter}
              onChange={setYearFilter}
            >
              <Option value="all">{t("CommercialDateAnalysis-ViewAll")}</Option>
              {availableYears.map((y) => (
                <Option key={y} value={y}>
                  {y}
                </Option>
              ))}
            </Select>
          </div>
        </div>
      </Card>

      <div className={styles.cardsGrid}>
        {filtered.length === 0 ? (
          <div className={styles.emptyState}>{t("CommercialDateAnalysis-NoResults")}</div>
        ) : (
          filtered.map((cd) => (
            <CommercialDateCard
              key={cd.id}
              card={cd}
              onOpen={() =>
                navigate(`/commercial-analysis/commercial-dates/${cd.id}?year=${cd.editionYear}`)
              }
            />
          ))
        )}
      </div>
    </div>
  );
};

export default CommercialDateAnalysis;
